import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import Icon, { icons } from '../../generic/Icon';
import { ICON_SIZE } from '../../generic/Icon/icon-constants';
import { Menu, MenuButton, MenuRadioGroup, RadioMenuItem } from '../../generic/Menu';
import type { FossabotPRSort } from '../../../../shared/fossabot/common';

import styles from './FossabotPrsSortMenu.module.less';

/** Listed in the order the menu shows them, with the page's default first. */
const SORT_OPTIONS: FossabotPRSort[] = ['newest', 'oldest'];

const SORT_LABELS: Record<FossabotPRSort, string> = {
  newest: 'Newest',
  oldest: 'Oldest',
};

/*
 * @szhsin/react-menu types a radio group's onRadioChange event value as `any`,
 * since it is generic over whatever a consumer puts in a MenuItem's value.
 * This menu only ever renders SORT_OPTIONS as values, so narrowing here is
 * just making that guarantee explicit before it reaches onSortChange.
 */
const SORT_OPTION_SET = new Set<string>(SORT_OPTIONS);

const isSortOption = (value: unknown): value is FossabotPRSort =>
  _.isString(value) && SORT_OPTION_SET.has(value);

type Props = {
  /** The order being applied, which the button reads out and the menu marks as checked. */
  sort: FossabotPRSort;
  /** Called with the picked order, including when it is the one already applied. */
  onSortChange: (sort: FossabotPRSort) => void;
};

/**
 * The order control on the right of the PR table header.
 *
 * A MenuButton rather than a bare button: only that component carries the
 * aria-haspopup and aria-expanded wiring that says the control opens a menu and
 * whether it is open, and it is a real button, so it is reachable by tab and
 * opens on Enter and Space.
 *
 * Its own text is the accessible name, which is why there is no aria-label on
 * it: one would replace "Sort by: Newest" and take the current order with it.
 * The chips beside it stay a group of aria-pressed toggles, so the two sit side
 * by side as separate controls rather than one becoming part of the other.
 *
 * The popup is named separately because it only borrows the button's text when
 * that text is a single string; the button holds an icon, a label and a caret,
 * so without a name of its own the popup announces as the literal "Menu".
 */
export const FossabotPrsSortMenu: VoidFunctionComponent<Props> = ({ sort, onSortChange }) => (
  <Menu
    aria-label="Sort by"
    direction="bottom"
    align="end"
    menuButton={(
      <MenuButton className={styles.sortButton}>
        <Icon icon={icons.sortDescending} />
        <span>{`Sort by: ${SORT_LABELS[sort]}`}</span>
        <Icon icon={icons.caretDown} size={ICON_SIZE.SM} />
      </MenuButton>
    )}
  >
    {/* Without a name of its own the radio group is announced as "Radio group". */}
    <MenuRadioGroup
      aria-label="Sort by"
      value={sort}
      onRadioChange={(event) => {
        if (isSortOption(event.value)) {
          onSortChange(event.value);
        }
      }}
    >
      {SORT_OPTIONS.map((option) => (
        <RadioMenuItem key={option} value={option}>{SORT_LABELS[option]}</RadioMenuItem>
      ))}
    </MenuRadioGroup>
  </Menu>
);
